"use client";

import React, { useEffect, useState, Suspense, lazy, useContext } from "react";
import {
  Card,
  Box,
  Typography,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isoWeek from "dayjs/plugin/isoWeek";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { AuthContext } from "../../../authentication/AuthContext";
import dashboardService from "../../../services/dashboardService";

// ✅ Lazy load Chart
const Chart = lazy(() => import("react-apexcharts"));

dayjs.extend(isBetween);
dayjs.extend(quarterOfYear);
dayjs.extend(isoWeek);

const RevenueChart = ({ data: initialRevenueData = [] }) => {
  const { user } = useContext(AuthContext);
  const [isChartLoaded, setChartLoaded] = useState(false);
  const [revenueData, setRevenueData] = useState(initialRevenueData);
  const [filter, setFilter] = useState("Weekly");
  const [previousFilter, setPreviousFilter] = useState("Weekly"); // Track last applied filter
  const [openModal, setOpenModal] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [appliedRange, setAppliedRange] = useState(null);

  useEffect(() => {
    setChartLoaded(true);
  }, []);

  // Fetch revenue data when filter changes
  useEffect(() => {
    const fetchRevenueData = async () => {
      if (!user?.client_id) return;

      try {
        let startDate = null;
        let endDate = null;

        // Calculate date range based on filter
        if (filter === "Weekly") {
          const today = dayjs();
          startDate = today.startOf("week").format("YYYY-MM-DD");
          endDate = today.endOf("week").format("YYYY-MM-DD");
        } else if (filter === "Monthly") {
          const today = dayjs();
          startDate = today.startOf("month").format("YYYY-MM-DD");
          endDate = today.endOf("month").format("YYYY-MM-DD");
        } else if (filter === "Quarterly") {
          const today = dayjs();
          startDate = today.startOf("quarter").format("YYYY-MM-DD");
          endDate = today.endOf("quarter").format("YYYY-MM-DD");
        } else if (filter === "Custom" && appliedRange) {
          startDate = dayjs(appliedRange.from).format("YYYY-MM-DD");
          endDate = dayjs(appliedRange.to).format("YYYY-MM-DD");
        }

        if (startDate && endDate) {
          const response = await dashboardService.getDashboardMetrics(
            user.client_id,
            startDate,
            endDate
          );

          if (response.success && response.data?.revenue_chart) {
            setRevenueData(response.data.revenue_chart);
            console.log("✅ Revenue data fetched successfully");
          }
        }
      } catch (error) {
        console.error("❌ Error fetching revenue data:", error);
      }
    };

    fetchRevenueData();
  }, [filter, appliedRange, user?.client_id]);

  const rawData = (revenueData || []).map((item) => ({
    ...item,
    date: dayjs(item.day || item.date),
    revenue: item.revenue_in_thousands || item.revenue || 0
  }));

  const today = dayjs();
  const startOfWeek = today.startOf("week");
  const endOfWeek = today.endOf("week");
  const startOfMonth = today.startOf("month");
  const endOfMonth = today.endOf("month");
  const startOfQuarter = today.startOf("quarter");
  const endOfQuarter = today.endOf("quarter");

  // Determine filtered data
  const getFilteredData = () => {
    let filtered = [];

    if (filter === "Weekly") {
      filtered = rawData.filter((d) =>
        d.date.isBetween(startOfWeek, endOfWeek, "day", "[]")
      );
    } else if (filter === "Monthly") {
      filtered = rawData.filter((d) =>
        d.date.isBetween(startOfMonth, endOfMonth, "day", "[]")
      );
    } else if (filter === "Quarterly") {
      filtered = rawData.filter((d) =>
        d.date.isBetween(startOfQuarter, endOfQuarter, "day", "[]")
      );
    } else if (filter === "Custom" && appliedRange) {
      const from = dayjs(appliedRange.from);
      const to = dayjs(appliedRange.to);
      filtered = rawData.filter((d) =>
        d.date.isBetween(from, to, "day", "[]")
      );
    } else {
      // If Custom selected but range not applied yet, keep previous filter
      return getPreviousFilteredData();
    }

    return filtered;
  };

  const getPreviousFilteredData = () => {
    // Use the previous filter to get data
    let prev = previousFilter;
    let filtered = [];
    if (prev === "Weekly") {
      filtered = rawData.filter((d) =>
        d.date.isBetween(startOfWeek, endOfWeek, "day", "[]")
      );
    } else if (prev === "Monthly") {
      filtered = rawData.filter((d) =>
        d.date.isBetween(startOfMonth, endOfMonth, "day", "[]")
      );
    } else if (prev === "Quarterly") {
      filtered = rawData.filter((d) =>
        d.date.isBetween(startOfQuarter, endOfQuarter, "day", "[]")
      );
    }
    return filtered;
  };

  const filteredData = getFilteredData();

  // Prepare chart data
  let categories = [];
  let data = [];

  if (filter === "Weekly" || (filter === "Custom" && !appliedRange && previousFilter === "Weekly")) {
    const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    categories = weekdays;
    data = weekdays.map((day) => {
      const dayData = filteredData.filter((d) => d.date.format("ddd") === day);
      return dayData.reduce((sum, d) => sum + d.revenue, 0);
    });
  } else if (filter === "Monthly" || (filter === "Custom" && !appliedRange && previousFilter === "Monthly")) {
    const grouped = {};
    filteredData.forEach((d) => {
      const weekStart = d.date.startOf("week");
      const label = weekStart.format("MMM D");
      grouped[label] = (grouped[label] || 0) + d.revenue;
    });
    categories = Object.keys(grouped);
    data = Object.values(grouped);
  } else if (filter === "Quarterly" || (filter === "Custom" && !appliedRange && previousFilter === "Quarterly")) {
    const months = {};
    filteredData.forEach((d) => {
      const month = d.date.format("MMM");
      months[month] = (months[month] || 0) + d.revenue;
    });
    categories = Object.keys(months);
    data = Object.values(months);
  } else {
    if (filteredData.length <= 10) {
      categories = filteredData.map((d) => d.date.format("DD/MM"));
      data = filteredData.map((d) => d.revenue);
    } else {
      const weeks = {};
      filteredData.forEach((d) => {
        const week = d.date.isoWeek();
        weeks[week] = (weeks[week] || 0) + d.revenue;
      });
      categories = Object.keys(weeks).map((w) => `Week ${w}`);
      data = Object.values(weeks);
    }
  }

  const series = [{ name: "Revenue", data }];

  const options = {
    chart: { zoom: { enabled: false }, toolbar: { show: true } },
    dataLabels: { enabled: false },
    colors: ["#605DFF"],
    stroke: { curve: "smooth" },
    title: {
      text: "Revenue Trends",
      align: "left",
      offsetX: -9,
      style: { fontWeight: "500", fontSize: "14px", color: "#64748B" },
    },
    xaxis: {
      categories,
      axisTicks: { show: false },
      axisBorder: { show: false },
      labels: { show: true, style: { colors: "#8695AA", fontSize: "12px" } },
    },
    grid: { show: true, borderColor: "#ECEEF2" },
    yaxis: {
      opposite: true,
      labels: { show: true, style: { colors: "#64748B", fontSize: "12px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
  };

  const handleFilterChange = (event) => {
    const value = event.target.value;
    if (value === "Custom") {
      setOpenModal(true);
    } else {
      setPreviousFilter(value);
    }
    setFilter(value);
  };

  const handleApplyCustom = () => {
    if (fromDate && toDate) {
      // Store dates as dayjs objects for proper handling
      setAppliedRange({ from: fromDate, to: toDate });
      setPreviousFilter("Custom"); // Mark custom as applied
    }
    setOpenModal(false);
  };

  return (
    <>
      <Card sx={{ boxShadow: "none", borderRadius: "7px", mb: "25px", p: { xs: 2, sm: 3, lg: 4 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb="25px">
          <Typography variant="h3" sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 700 }}>
            Revenue Chart
          </Typography>
          <Select size="small" value={filter} onChange={handleFilterChange} sx={{ minWidth: 140 }}>
            <MenuItem value="Weekly">Weekly</MenuItem>
            <MenuItem value="Monthly">Monthly</MenuItem>
            <MenuItem value="Quarterly">Quarterly</MenuItem>
            <MenuItem value="Custom">Custom</MenuItem>
          </Select>
        </Box>

        <Box sx={{ mb: "-15px", minHeight: 350 }}>
          {data.length > 0 ? (
            isChartLoaded && (
              <Suspense fallback={<div>Loading chart...</div>}>
                <Chart options={options} series={series} type="area" height={350} width="100%" />
              </Suspense>
            )
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={350} gap={1} sx={{ color: "#64748B" }}>
              <WarningAmberIcon sx={{ fontSize: 40, color: "#F59E0B" }} />
              <Typography variant="body1" fontWeight={500}>No data available</Typography>
              <Typography variant="body2">Change the date range to fetch more data</Typography>
            </Box>
          )}
        </Box>
      </Card>

      {/* Custom Range Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="sm">
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={(newValue) => setFromDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small", sx: { width: "100%" } }, actionBar: { actions: ["clear"] } }}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={(newValue) => setToDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small", sx: { width: "100%" } }, actionBar: { actions: ["clear"] } }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)} color="inherit">Cancel</Button>
          <Button onClick={handleApplyCustom} variant="contained" sx={{ backgroundColor: "#605DFF" }}>Apply</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RevenueChart;
